import { lazy, Suspense, useState, type ComponentType, type PropsWithChildren } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider as ReactAriaRouterProvider } from 'react-aria-components';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { RealtimeSync } from '../features/realtime/RealtimeSync';
import { RegistrationGate } from '../features/session/RegistrationGate';
import { SessionProvider, useSession } from '../features/session/SessionContext';
import { ActivityTracker } from '../services/activity/ActivityTracker';
import type { SessionGateway } from '../services/appwrite/session-gateway';
import { createQueryClient } from '../services/query/query-client';
import { AnnouncerProvider } from '../shared/accessibility/Announcer';
import { ToastProvider } from '../shared/accessibility/Toast';
import { SiteFooter } from '../shared/layout/SiteFooter';
import { SiteHeader } from '../shared/layout/SiteHeader';

const HomePage = lazy(() => import('../surfaces/home/HomePage'));
const PanelPage = lazy(() => import('../surfaces/panel/PanelPage'));
const AdminPage = lazy(() => import('../surfaces/admin/AdminPage'));
const SystemsPage = lazy(() => import('../surfaces/systems/SystemsPage'));
const ContactPage = lazy(() => import('../pages/contact/ContactPage'));
const BoardPage = lazy(() => import('../pages/board/BoardPage'));
const PostPage = lazy(() => import('../pages/post/PostPage'));
const ActivitiesPage = lazy(() => import('../pages/activities/ActivitiesPage'));
const ActivityPage = lazy(() => import('../pages/activities/ActivityPage'));
const ProfilePage = lazy(() => import('../pages/profile/ProfilePage'));
const PollsListPage = lazy(() => import('../pages/polls/PollsListPage'));
const PollPage = lazy(() => import('../pages/polls/PollPage'));
const LessonsListPage = lazy(() => import('../pages/lessons/LessonsListPage'));
const LessonPage = lazy(() => import('../pages/lessons/LessonPage'));

interface AppProps {
  gateway?: SessionGateway;
  /** Router alternativo. Las pruebas pasan `MemoryRouter` para fijar la ruta. */
  router?: ComponentType<PropsWithChildren>;
}

function HomeRoutes() {
  return (
    <Routes>
      <Route index element={<HomePage />} />
      <Route path="contact" element={<ContactPage />} />
      <Route path="activities" element={<ActivitiesPage />} />
      <Route path="activities/:activityId" element={<ActivityPage />} />
      <Route path="polls" element={<PollsListPage />} />
      <Route path="polls/:pollId" element={<PollPage />} />
      <Route path="lessons" element={<LessonsListPage />} />
      <Route path="lessons/:lessonId" element={<LessonPage />} />
      <Route path="users/:profileRef" element={<ProfilePage />} />
      {/* Los tablones viven bajo "/post" para no competir por rutas de un
          solo segmento con el resto del sitio (antes ":boardId" absorbía
          cualquier ruta de nivel superior, con o sin sentido). */}
      <Route path="post" element={<BoardPage boardId="post" />} />
      <Route path="post/:boardId" element={<BoardPage />} />
      <Route path="post/:boardId/:slugOrId" element={<PostPage />} />
    </Routes>
  );
}

/** Manda `/messages/...`, `/chat/...`, etc. (sin el prefijo) a su
 * equivalente bajo `/panel` — quedan enlaces viejos así guardados en
 * notificaciones ya creadas, además de cualquier lugar del código que se
 * nos haya escapado sin el prefijo. `/admin` no entra acá: tiene su propio
 * origen (ver `AdminPage` más abajo), no vive bajo `/panel`. */
function RedirectToPanel() {
  const location = useLocation();
  return <Navigate to={`/panel${location.pathname}${location.search}`} replace />;
}

// Rutas que solo existen dentro del panel — si aparecen sueltas (sin
// "/panel" adelante), es la misma página, mal referenciada.
const PANEL_ONLY_PREFIXES = ['messages', 'chat', 'comunidad', 'notifications'];

function Surface() {
  // Tres orígenes: "/panel" es el panel de la comunidad, "/admin" es la
  // administración del equipo, cualquier otra ruta es la superficie pública
  // — nada de subdominios separados por ahora (ida y vuelta entre dominios
  // complicaba más de lo que resolvía).
  return (
    <Routes>
      <Route path="panel/*" element={<PanelPage />} />
      <Route path="admin/*" element={<AdminPage />} />
      <Route path="systems/*" element={<SystemsPage />} />
      {PANEL_ONLY_PREFIXES.map((prefix) => (
        <Route key={prefix} path={`${prefix}/*`} element={<RedirectToPanel />} />
      ))}
      <Route path="*" element={<HomeRoutes />} />
    </Routes>
  );
}

/** Hace que los enlaces de React Aria usen el enrutador sin recargar el sitio. */
function AccessibleRouterProvider({ children }: PropsWithChildren) {
  const navigate = useNavigate();
  return (
    <ReactAriaRouterProvider navigate={(href) => void navigate(href)}>
      {children}
    </ReactAriaRouterProvider>
  );
}

/**
 * Una cuenta autenticada sin fila de perfil todavía no puede usar el sitio:
 * ve **solo** el formulario de registro, sin cabecera ni rutas, para que no
 * pueda navegar a ningún lado sin completarlo.
 */
function AppShell() {
  const { state } = useSession();
  if (state.status === 'authenticated' && !state.profile.profile.exists) {
    return <RegistrationGate />;
  }
  return (
    <>
      <a className="visually-hidden-focusable skip-link" href="#main-content">Saltar al contenido principal</a>
      <SiteHeader />
      <main id="main-content" className="container site-main" tabIndex={-1}>
        <Suspense fallback={<p role="status">Cargando contenido…</p>}>
          <Surface />
        </Suspense>
      </main>
      <SiteFooter />
    </>
  );
}

export default function App({ gateway, router: Router = BrowserRouter }: AppProps) {
  const [queryClient] = useState(createQueryClient);

  return (
    <AnnouncerProvider>
      <ToastProvider>
        <QueryClientProvider client={queryClient}>
          <SessionProvider gateway={gateway}>
            <Router>
              <AccessibleRouterProvider>
                <RealtimeSync />
                <ActivityTracker />
                <AppShell />
              </AccessibleRouterProvider>
            </Router>
          </SessionProvider>
        </QueryClientProvider>
      </ToastProvider>
    </AnnouncerProvider>
  );
}
